import { __awaiter, __decorate, __generator, __read, __values } from "tslib";
import { QueryAggregateType, TransactionMode } from '@tencent/memoire-core';
import QueryExecutor from './query-executor';
import QueryRewriter from './query-rewriter';
import QueryBinder from './query-binder';
import QueryPlanner from './query-planner';
import { convertPersistentDataToModel } from '../utils/model-data-converter';
import QueryExpEliminator from './query-exp-eliminator';
import memo from '../utils/memo';
import QueryOptimizer from './query-optimizer';
import RawQueryBuilder from './raw-query-builder';
import { TableOperationUtils } from '../model';
export var UNLIMITED = undefined;
var OptimizationMode;
(function (OptimizationMode) {
    OptimizationMode["DISABLED"] = "disabled";
    OptimizationMode["ALL"] = "all";
})(OptimizationMode || (OptimizationMode = {}));
var QueryDelegate = /** @class */ (function () {
    function QueryDelegate(collectionName, options) {
        this.collectionName = collectionName;
        this.context = options.context;
    }
    QueryDelegate.toggleOptimization = function (enabled) {
        QueryDelegate.optimization = enabled ? OptimizationMode.ALL : OptimizationMode.DISABLED;
    };
    QueryDelegate.getOptimizationMode = function () {
        return QueryDelegate.optimization;
    };
    QueryDelegate.prototype.exec = function (rawQuery, aggregate) {
        return __awaiter(this, void 0, void 0, function () {
            var transaction, model, aliasMap, query, table, values, entries, plan, executor, _a, pointers;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.context.getTransactionGenerator([this.collectionName], TransactionMode.READ_ONLY)];
                    case 1:
                        transaction = (_b.sent()).startTransaction();
                        return [4 /*yield*/, this.getModel()];
                    case 2:
                        model = _b.sent();
                        aliasMap = this.getAliasMap(model.class);
                        query = RawQueryBuilder.fromRawQuery(rawQuery, aliasMap).build();
                        table = transaction.table(this.collectionName);
                        if (!(table.supports('query') && query.whereClauses.length <= 1)) return [3 /*break*/, 7];
                        if (!(aggregate === QueryAggregateType.VALUES)) return [3 /*break*/, 4];
                        return [4 /*yield*/, table.query(query, aggregate)];
                    case 3:
                        values = _b.sent();
                        return [2 /*return*/, values.map(function (value) { var _a; return (_a = convertPersistentDataToModel(value, model.class)) !== null && _a !== void 0 ? _a : undefined; })];
                    case 4:
                        if (!(aggregate === QueryAggregateType.ENTRIES)) return [3 /*break*/, 6];
                        return [4 /*yield*/, table.query(query, aggregate)];
                    case 5:
                        entries = _b.sent();
                        return [2 /*return*/, entries.map(function (_a) {
                                var _b;
                                var _c = __read(_a, 2), key = _c[0], value = _c[1];
                                return [key, (_b = convertPersistentDataToModel(value, model.class)) !== null && _b !== void 0 ? _b : undefined];
                            })];
                    case 6: return [2 /*return*/, table.query(query, aggregate)];
                    case 7: return [4 /*yield*/, this.getQueryPlan(query, aggregate)];
                    case 8:
                        plan = _b.sent();
                        executor = new QueryExecutor(plan, transaction);
                        return [4 /*yield*/, executor.exec()];
                    case 9:
                        _a = __read.apply(void 0, [_b.sent(), 1]), pointers = _a[0];
                        if (aggregate === QueryAggregateType.COUNT) {
                            return [2 /*return*/, pointers.length];
                        }
                        if (aggregate === QueryAggregateType.KEYS) {
                            return [2 /*return*/, pointers.map(function (_a) {
                                    var _b = __read(_a, 1), key = _b[0];
                                    return key;
                                })];
                        }
                        if (aggregate === QueryAggregateType.VALUES) {
                            return [2 /*return*/, pointers.map(function (_a) {
                                    var _b;
                                    var _c = __read(_a, 2), value = _c[1];
                                    return (_b = convertPersistentDataToModel(value, model.class)) !== null && _b !== void 0 ? _b : undefined;
                                })];
                        }
                        return [2 /*return*/, pointers.map(function (_a) {
                                var _b;
                                var _c = __read(_a, 2), key = _c[0], value = _c[1];
                                return [key, (_b = convertPersistentDataToModel(value, model.class)) !== null && _b !== void 0 ? _b : undefined];
                            })];
                }
            });
        });
    };
    QueryDelegate.prototype.conditions = function (rawQuery) {
        var rewriter = this.getRewriter();
        return rewriter.rewrite(rawQuery);
    };
    QueryDelegate.prototype.explain = function (rawQuery, aggregate) {
        return __awaiter(this, void 0, void 0, function () {
            var plan;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getQueryPlan(rawQuery, aggregate)];
                    case 1:
                        plan = _a.sent();
                        return [2 /*return*/, plan];
                }
            });
        });
    };
    QueryDelegate.prototype.getQueryPlan = function (rawQuery, aggregate) {
        return __awaiter(this, void 0, void 0, function () {
            var rewriter, eliminator, planner, binder, optimizer, conditions, plan;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getRewriter()];
                    case 1:
                        rewriter = _a.sent();
                        return [4 /*yield*/, this.getEliminator()];
                    case 2:
                        eliminator = _a.sent();
                        return [4 /*yield*/, this.getPlanner()];
                    case 3:
                        planner = _a.sent();
                        return [4 /*yield*/, this.getBinder()];
                    case 4:
                        binder = _a.sent();
                        return [4 /*yield*/, this.getOptimizer()];
                    case 5:
                        optimizer = _a.sent();
                        conditions = rewriter.rewrite(rawQuery);
                        conditions = binder.bind(conditions);
                        conditions = eliminator.eliminate(conditions);
                        plan = planner.plan(conditions, aggregate);
                        return [2 /*return*/, QueryDelegate.optimization === OptimizationMode.DISABLED ? plan : optimizer.optimize(plan)];
                }
            });
        });
    };
    QueryDelegate.prototype.getAliasMap = function (modelClass) {
        var e_1, _a;
        var aliasMap = new Map();
        var tableInfo = TableOperationUtils.getTableInfo(modelClass.prototype);
        if (!tableInfo) {
            return aliasMap;
        }
        try {
            for (var _b = __values(tableInfo.properties.values()), _c = _b.next(); !_c.done; _c = _b.next()) {
                var prop = _c.value;
                if (prop.alias) {
                    aliasMap.set(prop.name, prop.alias);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return aliasMap;
    };
    QueryDelegate.prototype.getModel = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.context.waitForModelRegistered(this.collectionName)];
            });
        });
    };
    QueryDelegate.prototype.getPlanner = function () {
        return __awaiter(this, void 0, void 0, function () {
            var model;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getModel()];
                    case 1:
                        model = _a.sent();
                        return [2 /*return*/, new QueryPlanner(model.schema, this.context.comparer)];
                }
            });
        });
    };
    QueryDelegate.prototype.getRewriter = function () {
        return new QueryRewriter(this.context.comparer);
    };
    QueryDelegate.prototype.getEliminator = function () {
        return new QueryExpEliminator();
    };
    QueryDelegate.prototype.getBinder = function () {
        return __awaiter(this, void 0, void 0, function () {
            var model;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getModel()];
                    case 1:
                        model = _a.sent();
                        return [2 /*return*/, new QueryBinder(model.schema)];
                }
            });
        });
    };
    QueryDelegate.prototype.getOptimizer = function () {
        return new QueryOptimizer();
    };
    QueryDelegate.optimization = OptimizationMode.ALL;
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getModel", null);
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getPlanner", null);
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getRewriter", null);
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getEliminator", null);
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getBinder", null);
    __decorate([
        memo()
    ], QueryDelegate.prototype, "getOptimizer", null);
    return QueryDelegate;
}());
export default QueryDelegate;
//# sourceMappingURL=query-delegate.js.map